package com.evoapps.evoshift.domain.explain

import com.evoapps.evoshift.domain.staffing.DayStaffingHint

/**
 * SH-152: a day's staffing hint explained: its labour % is the planned labour cost (the assigned
 * shifts' cost snapshots and the unassigned shifts at their estimate) ÷ the income the expected
 * sales bring (the commission on them, or the sales without a commission, plus the day's fixed
 * revenue), as StaffingRules.hint works it out.
 */
object StaffingExplanations {
    fun labourPercent(hint: DayStaffingHint): Ratio {
        val openCost = hint.openCost
        val labour = buildList {
            add(Term(TermKind.PLANNED_SHIFTS, hint.plannedCost, hours = hint.plannedHours))
            if (hint.openHours > 0) {
                add(
                    if (openCost != null) Term(TermKind.OPEN_SHIFTS, openCost, hours = hint.openHours, rate = openCost / hint.openHours)
                    else Term(TermKind.OPEN_SHIFTS_NOT_PRICED, 0.0, Sign.NOT_COUNTED, hours = hint.openHours)
                )
            }
            if (hint.unpricedShifts > 0) {
                if (hint.unpricedUserIds.isEmpty()) {
                    add(Term(TermKind.PERSON_WITHOUT_PAY, 0.0, Sign.NOT_COUNTED, hours = hint.unpricedHours, shifts = hint.unpricedShifts))
                } else {
                    hint.unpricedUserIds.forEach { add(Term(TermKind.PERSON_WITHOUT_PAY, 0.0, Sign.NOT_COUNTED, personId = it)) }
                }
            }
        }
        val sales = hint.expectedSales.amount
        val salesIncome = hint.expectedIncome - hint.fixedRevenue
        val income = listOf(
            if (hint.commissionPercent > 0) Term(TermKind.COMMISSION, salesIncome, percent = hint.commissionPercent * 100, base = sales)
            else Term(TermKind.NET_SALES_KEPT, salesIncome, base = sales),
            Term(TermKind.FIXED_REVENUE, hint.fixedRevenue)
        )
        return Ratio(
            Figure.STAFFING_LABOUR_PERCENT, Side.PLAN,
            Breakdown.of(Figure.LABOUR_COST, Side.PLAN, hint.plannedCost + (openCost ?: 0.0), labour),
            Breakdown.of(Figure.INCOME, Side.PLAN, hint.expectedIncome, income)
        )
    }
}
